import * as THREE from 'three';
import type { Vehicle } from '@polyloop/sim';

const FORWARD = new THREE.Vector3(0, 0, 1); // chase from BEHIND, so behind car is +Z in local
const UP = new THREE.Vector3(0, 1, 0);

/**
 * Critically-damped 3rd-person chase camera with dynamic FOV by speed.
 *
 *  - Position trails behind car along ground-aligned forward.
 *  - Lookahead: subtly aims toward where the car is going.
 *  - FOV punches outward on boost / high speed.
 */
export class ChaseCamera {
  readonly camera: THREE.PerspectiveCamera;
  baseFov = 72;
  boostFov = 86;

  private currentFov = 72;
  private posDamper = new Damper3(8.0);
  private targetDamper = new Damper3(12.0);

  // Tunables
  trailDistance = 6.2;
  height = 2.6;
  lookAhead = 4.0;

  /** Cinematic mode = high overview camera for demo / spectator screenshots. */
  setCinematic(on: boolean): void {
    if (on) {
      this.trailDistance = 16;
      this.height = 9;
      this.lookAhead = 6;
      this.baseFov = 60;
      this.boostFov = 72;
    } else {
      this.trailDistance = 6.2;
      this.height = 2.6;
      this.lookAhead = 4.0;
      this.baseFov = 72;
      this.boostFov = 86;
    }
  }

  constructor(camera: THREE.PerspectiveCamera) {
    this.camera = camera;
    this.currentFov = camera.fov;
  }

  update(
    carPos: THREE.Vector3,
    carQuat: THREE.Quaternion,
    vehicle: Vehicle,
    dt: number,
  ): void {
    const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(carQuat);
    fwd.y = 0;
    fwd.normalize();

    const trail = fwd.clone().multiplyScalar(-this.trailDistance);
    const targetCamPos = carPos.clone().add(trail);
    targetCamPos.y += this.height;

    const lookAt = carPos.clone().add(fwd.multiplyScalar(this.lookAhead));
    lookAt.y += 0.6;

    const dampedPos = this.posDamper.update(this.camera.position, targetCamPos, dt);
    this.camera.position.copy(dampedPos);

    const dampedTarget = this.targetDamper.update(this._lookTmp, lookAt, dt);
    this._lookTmp.copy(dampedTarget);
    this.camera.lookAt(this._lookTmp);

    // FOV punch by speed + boost.
    const speedFrac = Math.min(1, vehicle.snapshot().speedMs / 35);
    const targetFov = lerp(this.baseFov, this.boostFov, speedFrac);
    this.currentFov = lerp(this.currentFov, targetFov, Math.min(1, dt * 5));
    if (Math.abs(this.camera.fov - this.currentFov) > 0.05) {
      this.camera.fov = this.currentFov;
      this.camera.updateProjectionMatrix();
    }

    // Reference unused imports so build keeps them; helps DCE clarity.
    void FORWARD;
    void UP;
  }

  private _lookTmp = new THREE.Vector3();
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

class Damper3 {
  private vel = new THREE.Vector3();
  constructor(public rate: number) {}
  update(current: THREE.Vector3, target: THREE.Vector3, dt: number): THREE.Vector3 {
    // Critically-damped smoothing.
    const f = 1 - Math.exp(-this.rate * dt);
    const dx = (target.x - current.x) * f;
    const dy = (target.y - current.y) * f;
    const dz = (target.z - current.z) * f;
    current.x += dx;
    current.y += dy;
    current.z += dz;
    return current;
  }
}
